import { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, Cake, Send, FileDown, Save, Users, GraduationCap, CalendarDays, Trash2, Plus, ScrollText } from "lucide-react";
import { Modal, useFetch } from "@/lib/ui";

const TAB = { UPCOMING: "upcoming", SEND: "send", OFFICIAL: "official", DOB: "dob", TEMPLATES: "templates", HISTORY: "history" };

export default function NotificationCenter() {
  const { L } = useLang();
  const [tab, setTab] = useState(TAB.UPCOMING);
  const [birthdays, setBirthdays] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [bdates, setBdates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    await refresh();
    setLoading(false);
  };
  const refresh = async () => {
    try {
      const [b, t, h, d] = await Promise.all([
        api.get("/notifications/upcoming-birthdays", { params: { days: 30 } }),
        api.get("/letter-templates"),
        api.get("/notifications/history"),
        api.get("/notifications/birthdates"),
      ]);
      setBirthdays(b); setTemplates(t); setHistory(h); setBdates(d);
    } catch (e) { toast.error(L("Failed to load", "Échec du chargement")); }
  };
  useEffect(() => { load(); }, []);

  const runBirthdaysNow = async () => {
    try {
      const r = await api.post("/notifications/run-birthdays");
      toast.success(L(`Processed ${r.processed} birthday(s)`, `${r.processed} anniversaire(s) traité(s)`));
      refresh();
    } catch (e) { toast.error(L("Failed", "Échec")); }
  };

  const tabs = [
    { key: TAB.UPCOMING, label: L("Upcoming Birthdays", "Anniversaires à venir"), icon: Cake },
    { key: TAB.SEND, label: L("Send a Letter", "Envoyer une lettre"), icon: Send },
    { key: TAB.OFFICIAL, label: L("Official Letter", "Lettre Officielle"), icon: ScrollText },
    { key: TAB.DOB, label: L("Birth Dates", "Dates de naissance"), icon: CalendarDays },
    { key: TAB.TEMPLATES, label: L("Letter Templates", "Modèles de lettres"), icon: FileDown },
    { key: TAB.HISTORY, label: L("History", "Historique"), icon: Bell },
  ];

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;

  return (
    <div className="animate-fade-up" data-testid="notification-center">
      <PageHeader title={L("Notification Center", "Centre de notifications")}
        description={L("Birthday reminders and personalized ICDH letters for parents, teachers and students.",
          "Rappels d'anniversaire et lettres personnalisées ICDH pour parents, enseignants et élèves.")} />

      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`nc-tab-${tb.key}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition-colors ${tab === tb.key ? "bg-primary text-primary-foreground border-primary" : "border-input hover:bg-secondary"}`}>
            <tb.icon className="h-4 w-4" />{tb.label}
          </button>
        ))}
      </div>

      {tab === TAB.UPCOMING && <Upcoming birthdays={birthdays} onRun={runBirthdaysNow} templates={templates} onSent={refresh} L={L} />}
      {tab === TAB.SEND && <SendLetter templates={templates} onSent={refresh} L={L} />}
      {tab === TAB.OFFICIAL && <OfficialLetter onSent={refresh} L={L} />}
      {tab === TAB.DOB && <Birthdates rows={bdates} onChange={refresh} L={L} />}
      {tab === TAB.TEMPLATES && <Templates templates={templates} onSaved={refresh} L={L} />}
      {tab === TAB.HISTORY && <History history={history} L={L} />}
    </div>
  );
}

function LetterheadPreview() {
  const { data: s } = useFetch(() => api.get("/settings"));
  const school = s || {};
  return (
    <div className="rounded-t-md bg-[#001A5C] text-white px-5 py-4 flex items-center gap-4" data-testid="letterhead-preview">
      <img src={`${API}/settings/logo`} alt="" className="h-14 w-14 object-contain bg-white/10 rounded" />
      <div>
        <p className="font-heading font-bold text-lg leading-tight">{school.school_name || "Institution Chrétienne D'Haïti"}</p>
        <p className="text-[#C9A24B] text-sm italic">“{school.motto || "L'école de l'Avenir"}”</p>
        <p className="text-xs text-white/80">{school.address || "Carrefour, Haïti"}</p>
      </div>
    </div>
  );
}

function PeoplePicker({ title, people, selected, onToggle, testid, L }) {
  const [q, setQ] = useState("");
  const list = (people || []).filter((p) => !q || `${p.name} ${p.email || ""} ${p.sub || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border border-border rounded-md" data-testid={testid}>
      <div className="px-3 py-2 bg-secondary/50 text-sm font-medium flex items-center justify-between">
        <span>{title}</span><span className="text-xs text-muted-foreground">{selected.length} {L("selected", "sélectionné(s)")}</span>
      </div>
      <div className="p-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Search…", "Rechercher…")} className="h-8 mb-2" data-testid={`${testid}-search`} />
        <div className="max-h-40 overflow-auto divide-y divide-border">
          {list.length === 0 ? <p className="text-xs text-muted-foreground p-2">{L("None", "Aucun")}</p> : list.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-secondary" data-testid={`${testid}-row-${p.id}`}>
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => onToggle(p.id)} data-testid={`${testid}-chk-${p.id}`} className="h-4 w-4" />
              <span className="flex-1 min-w-0"><span className="font-medium">{p.name}</span>{p.sub ? <span className="block text-xs text-muted-foreground truncate">{p.sub}</span> : null}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function OfficialLetter({ onSent, L, preset, onClose }) {
  const [subject, setSubject] = useState(preset ? L("Birthday wishes", "Souhaits d'anniversaire") : "");
  const [bodyText, setBodyText] = useState("");
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selS, setSelS] = useState([]);
  const [selP, setSelP] = useState([]);
  const [selSt, setSelSt] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preset) return;
    (async () => {
      try {
        const [st, pa, te, sf] = await Promise.all([
          api.get("/students", { params: { status: "accepted" } }),
          api.get("/parents"), api.get("/teachers"), api.get("/staff"),
        ]);
        setStudents((Array.isArray(st) ? st : st.students || []).map((x) => ({ id: x.id, name: `${x.first_name} ${x.last_name}`, sub: x.grade_level })));
        setParents((pa || []).map((x) => ({ id: x.id, name: x.name, sub: x.email })));
        const staffList = [...(te || []).map((x) => ({ id: x.id, name: x.name, sub: L("Teacher", "Enseignant") })), ...(sf || []).map((x) => ({ id: x.id, name: x.name, sub: x.role || L("Staff", "Personnel") }))];
        const seen = {}; setStaff(staffList.filter((x) => (seen[x.id] ? false : (seen[x.id] = true))));
      } catch (e) { /* noop */ }
    })();
  }, [preset]);

  const toggle = (setter) => (id) => setter((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const send = async () => {
    if (!bodyText.trim()) { toast.error(L("Write a message", "Écrivez un message")); return; }
    let payload = { subject: subject || "ICDH", body: bodyText };
    if (preset) {
      payload.student_ids = preset.type === "student" ? [preset.id] : [];
      payload.parent_ids = preset.type === "parent" ? [preset.id] : [];
      payload.staff_ids = preset.type === "teacher" ? [preset.id] : [];
    } else {
      if (selS.length + selP.length + selSt.length === 0) { toast.error(L("Select at least one recipient", "Sélectionnez au moins un destinataire")); return; }
      payload = { ...payload, student_ids: selS, parent_ids: selP, staff_ids: selSt };
    }
    setSending(true);
    try {
      const r = await api.post("/official-letter/send", payload);
      toast.success(L(`Delivered to ${r.recipients} (email sent ${r.sent})`, `Livré à ${r.recipients} (email envoyé ${r.sent})`));
      setBodyText(""); setSelS([]); setSelP([]); setSelSt([]); onSent && onSent(); onClose && onClose();
    } catch (e) { toast.error(e.response?.data?.detail || L("Send failed", "Échec de l'envoi")); }
    setSending(false);
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="official-letter">
      <div className="border border-border rounded-md shadow-sm">
        <LetterheadPreview />
        <div className="p-4 space-y-3">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={L("Subject", "Objet")} data-testid="official-subject" />
          <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={10}
            placeholder={L("Write your official letter here…", "Rédigez votre lettre officielle ici…")}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-serif leading-relaxed" data-testid="official-body" />
        </div>
      </div>
      {preset ? (
        <p className="text-sm text-muted-foreground" data-testid="official-preset-note">{L("Recipient", "Destinataire")}: <b>{preset.name}</b> ({preset.type === "student" ? L("via parent inbox", "via boîte du parent") : preset.type})</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-3">
          <PeoplePicker title={L("Students", "Élèves")} people={students} selected={selS} onToggle={toggle(setSelS)} testid="pick-students" L={L} />
          <PeoplePicker title={L("Parents", "Parents")} people={parents} selected={selP} onToggle={toggle(setSelP)} testid="pick-parents" L={L} />
          <PeoplePicker title={L("Staff", "Personnel")} people={staff} selected={selSt} onToggle={toggle(setSelSt)} testid="pick-staff" L={L} />
        </div>
      )}
      <div className="flex gap-2">
        <Button onClick={send} disabled={sending} data-testid="official-send-btn"><Send className="h-4 w-4 mr-1" />{sending ? L("Sending…", "Envoi…") : L("Send official letter", "Envoyer la lettre")}</Button>
        {onClose && <Button variant="outline" onClick={onClose} data-testid="official-cancel-btn">{L("Cancel", "Annuler")}</Button>}
      </div>
    </div>
  );
}

function Upcoming({ birthdays, onRun, templates, onSent, L }) {
  const dayLabel = (d) => d === 0 ? L("Today", "Aujourd'hui") : d === 1 ? L("Tomorrow", "Demain") : L(`In ${d} days`, `Dans ${d} jours`);
  const [sel, setSel] = useState(null);      // birthday entry being acted on
  const [mode, setMode] = useState(null);    // "choose" | "custom"
  const [busy, setBusy] = useState(false);

  const sendGeneric = async () => {
    if (!sel) return;
    const key = `${sel.type}_birthday`;
    if (!templates.some((t) => t.key === key)) { toast.error(L("No template for this type", "Aucun modèle pour ce type")); return; }
    setBusy(true);
    try {
      await api.post("/notifications/send", { template_key: key, recipient_type: sel.type, recipient_ids: [sel.id], also_notify_parents: sel.type === "student" });
      toast.success(L("Generic birthday message sent", "Message d'anniversaire générique envoyé"));
      setSel(null); setMode(null); onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || L("Send failed", "Échec")); }
    setBusy(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-muted-foreground">{L("Next 30 days. Click a row to send a generic or custom letter.", "30 prochains jours. Cliquez une ligne pour envoyer un message générique ou personnalisé.")}</p>
        <Button size="sm" variant="outline" onClick={onRun} data-testid="run-birthdays-btn"><Cake className="h-4 w-4 mr-1" />{L("Send today's due now", "Envoyer les dus du jour")}</Button>
      </div>
      {birthdays.length === 0 ? <Empty title={L("No upcoming birthdays", "Aucun anniversaire à venir")} /> : (
        <Table>
          <TableHeader><TableRow><TableHead>{L("Name", "Nom")}</TableHead><TableHead>{L("Type", "Type")}</TableHead><TableHead>{L("Date of birth", "Date de naissance")}</TableHead><TableHead>{L("When", "Quand")}</TableHead><TableHead>{L("Email", "Email")}</TableHead></TableRow></TableHeader>
          <TableBody>{birthdays.map((b) => (
            <TableRow key={`${b.type}-${b.id}`} data-testid={`birthday-row-${b.id}`} className="cursor-pointer hover:bg-secondary/60" onClick={() => { setSel(b); setMode("choose"); }}>
              <TableCell className="font-medium">{b.name}</TableCell>
              <TableCell>{b.type === "student" ? <Badge variant="secondary"><GraduationCap className="h-3 w-3 mr-1" />{L("Student", "Élève")}</Badge> : b.type === "parent" ? <Badge className="bg-sky-600"><Users className="h-3 w-3 mr-1" />{L("Parent", "Parent")}</Badge> : <Badge><Users className="h-3 w-3 mr-1" />{L("Teacher", "Enseignant")}</Badge>}</TableCell>
              <TableCell>{b.dob}</TableCell>
              <TableCell>{b.days_until <= 1 ? <Badge className="bg-amber-500">{dayLabel(b.days_until)}</Badge> : dayLabel(b.days_until)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{b.email || L("no email", "sans email")}</TableCell>
            </TableRow>))}</TableBody>
        </Table>
      )}

      <Modal open={!!sel && mode === "choose"} onClose={() => { setSel(null); setMode(null); }} title={sel ? `${L("Birthday", "Anniversaire")} — ${sel.name}` : ""}>
        <div className="space-y-3" data-testid="birthday-actions">
          <p className="text-sm text-muted-foreground">{L("Choose how to celebrate this birthday.", "Choisissez comment célébrer cet anniversaire.")}</p>
          <Button className="w-full justify-start" variant="outline" disabled={busy} onClick={sendGeneric} data-testid="bday-generic-btn"><Cake className="h-4 w-4 mr-2" />{L("Send the generic message", "Envoyer le message générique")}</Button>
          <Button className="w-full justify-start" disabled={busy} onClick={() => setMode("custom")} data-testid="bday-custom-btn"><ScrollText className="h-4 w-4 mr-2" />{L("Write a personalized letter", "Écrire une lettre personnalisée")}</Button>
        </div>
      </Modal>

      <Modal open={!!sel && mode === "custom"} onClose={() => { setSel(null); setMode(null); }} title={sel ? `${L("Personalized letter", "Lettre personnalisée")} — ${sel.name}` : ""} wide>
        {sel && <OfficialLetter preset={sel} onSent={onSent} onClose={() => { setSel(null); setMode(null); }} L={L} />}
      </Modal>
    </div>
  );
}

function SendLetter({ templates, onSent, L }) {
  const [tplKey, setTplKey] = useState(templates[0]?.key || "");
  const [recType, setRecType] = useState("teacher");
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [alsoParents, setAlsoParents] = useState(true);
  const [customEmail, setCustomEmail] = useState("");
  const [customName, setCustomName] = useState("");
  const [sealSend, setSealSend] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPeople = async (type) => {
    setSelected([]);
    try {
      if (type === "student") {
        const s = await api.get("/students", { params: { status: "accepted" } });
        setPeople((Array.isArray(s) ? s : s.students || []).map((x) => ({ id: x.id, name: `${x.first_name} ${x.last_name}`, email: x.student_email })));
      } else if (type === "teacher") {
        const t = await api.get("/teachers");
        setPeople(t.map((x) => ({ id: x.id, name: x.name, email: x.email })));
      } else if (type === "parent") {
        const p = await api.get("/parents");
        setPeople((p || []).map((x) => ({ id: x.id, name: x.name, email: x.email })));
      } else { setPeople([]); }
    } catch (e) { setPeople([]); }
  };
  useEffect(() => { if (recType !== "custom") loadPeople(recType); }, [recType]);
  useEffect(() => {
    const match = `${recType}_birthday`;
    if (templates.some((t) => t.key === match)) setTplKey(match);
  }, [recType, templates]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const send = async () => {
    if (!tplKey) { toast.error(L("Pick a template", "Choisissez un modèle")); return; }
    const payload = { template_key: tplKey, recipient_type: recType };
    if (recType === "custom") {
      if (!customEmail) { toast.error(L("Enter an email", "Saisissez un email")); return; }
      payload.custom_email = customEmail; payload.custom_name = customName;
    } else {
      if (selected.length === 0) { toast.error(L("Select recipients", "Sélectionnez des destinataires")); return; }
      payload.recipient_ids = selected;
      if (recType === "student") payload.also_notify_parents = alsoParents;
    }
    setSending(true);
    try {
      const r = await api.post("/notifications/send", payload);
      toast.success(L(`Sent ${r.sent}, failed ${r.failed}`, `Envoyés ${r.sent}, échoués ${r.failed}`));
      setSelected([]); setCustomEmail(""); setCustomName(""); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || L("Send failed", "Échec de l'envoi")); }
    setSending(false);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1 block">{L("Letter template", "Modèle de lettre")}</label>
          <Select value={tplKey} onValueChange={setTplKey}>
            <SelectTrigger data-testid="nc-template-select"><SelectValue placeholder={L("Choose", "Choisir")} /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">{L("Recipient type", "Type de destinataire")}</label>
          <Select value={recType} onValueChange={setRecType}>
            <SelectTrigger data-testid="nc-rectype-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="teacher">{L("Teacher", "Enseignant")}</SelectItem>
              <SelectItem value="student">{L("Student", "Élève")}</SelectItem>
              <SelectItem value="parent">{L("Parent", "Parent")}</SelectItem>
              <SelectItem value="custom">{L("Custom email", "Email personnalisé")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {recType === "student" && (
          <label className="flex items-center gap-2 text-sm" data-testid="nc-also-parents">
            <input type="checkbox" checked={alsoParents} onChange={(e) => setAlsoParents(e.target.checked)} />
            {L("Also send to the student's parents", "Envoyer aussi aux parents de l'élève")}
          </label>
        )}
        {recType === "custom" ? (
          <div className="space-y-2">
            <Input placeholder={L("Recipient name", "Nom du destinataire")} value={customName} onChange={(e) => setCustomName(e.target.value)} data-testid="nc-custom-name" />
            <Input type="email" placeholder="email@exemple.com" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} data-testid="nc-custom-email" />
          </div>
        ) : (
          <div className="border border-border rounded-md max-h-72 overflow-auto" data-testid="nc-people-list">
            {people.length === 0 ? <p className="text-sm text-muted-foreground p-3">{L("No one found", "Personne trouvé")}</p> :
              people.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-0 text-sm cursor-pointer hover:bg-secondary">
                  <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`nc-person-${p.id}`} />
                  <span className="flex-1">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{p.email || L("no email", "sans email")}</span>
                </label>
              ))}
          </div>
        )}
        <Button onClick={send} disabled={sending} data-testid="nc-send-btn"><Send className="h-4 w-4 mr-1" />{sending ? L("Sending…", "Envoi…") : L("Send letter", "Envoyer la lettre")}</Button>
      </div>
      <div>
        <label className="text-sm font-medium mb-1 block">{L("Preview PDF", "Aperçu PDF")}</label>
        {tplKey ? (
          <a href={`${API}/letters/${tplKey}/pdf?name=${encodeURIComponent(customName || "")}&seal=${sealSend ? 1 : 0}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center text-primary underline text-sm" data-testid="nc-preview-pdf">
            <FileDown className="h-4 w-4 mr-1" />{L("Open the letter as PDF", "Ouvrir la lettre en PDF")}
          </a>
        ) : null}
        <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer" data-testid="nc-seal-wrap">
          <input type="checkbox" checked={sealSend} onChange={(e) => setSealSend(e.target.checked)} data-testid="nc-seal" />
          {L("Add the dated seal", "Ajouter le sceau daté")}
        </label>
        <p className="text-xs text-muted-foreground mt-2">{L("The email is sent privately to each recipient with their first name inserted automatically.", "L'email est envoyé individuellement à chaque destinataire, avec son prénom inséré automatiquement.")}</p>
      </div>
    </div>
  );
}

function Templates({ templates, onSaved, L }) {
  const [drafts, setDrafts] = useState(() => Object.fromEntries(templates.map((t) => [t.key, { subject: t.subject, body: t.body }])));
  const [sealOn, setSealOn] = useState(false);
  const save = async (key) => {
    try {
      await api.patch(`/letter-templates/${key}`, drafts[key]);
      toast.success(L("Template saved", "Modèle enregistré"));
      onSaved();
    } catch (e) { toast.error(L("Save failed", "Échec de l'enregistrement")); }
  };
  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-sm text-muted-foreground">{L("Use [Prénom] where the recipient's first name should appear. It is replaced automatically at send time.", "Utilisez [Prénom] là où le prénom du destinataire doit apparaître. Il est remplacé automatiquement à l'envoi.")}</p>
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer bg-amber-50 border border-amber-200 rounded-md px-3 py-2 w-fit" data-testid="letter-seal-wrap">
        <input type="checkbox" checked={sealOn} onChange={(e) => setSealOn(e.target.checked)} data-testid="letter-seal" />
        {L("Add the dated seal on the PDF", "Ajouter le sceau daté sur le PDF")}
      </label>
      {templates.map((t) => (
        <div key={t.key} className="border border-border rounded-lg p-4" data-testid={`template-card-${t.key}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{t.title}</h3>
            <div className="flex items-center gap-2">
              <a href={`${API}/letters/${t.key}/pdf?seal=${sealOn ? 1 : 0}`} target="_blank" rel="noreferrer" className="text-primary text-sm inline-flex items-center" data-testid={`template-pdf-${t.key}`}><FileDown className="h-4 w-4 mr-1" />PDF</a>
              <Button size="sm" onClick={() => save(t.key)} data-testid={`template-save-${t.key}`}><Save className="h-4 w-4 mr-1" />{L("Save", "Enregistrer")}</Button>
            </div>
          </div>
          <Input className="mb-2" value={drafts[t.key]?.subject ?? t.subject}
            onChange={(e) => setDrafts((d) => ({ ...d, [t.key]: { ...d[t.key], subject: e.target.value } }))}
            data-testid={`template-subject-${t.key}`} />
          <textarea rows={9} value={drafts[t.key]?.body ?? t.body}
            onChange={(e) => setDrafts((d) => ({ ...d, [t.key]: { ...d[t.key], body: e.target.value } }))}
            data-testid={`template-body-${t.key}`}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" />
        </div>
      ))}
    </div>
  );
}

function Birthdates({ rows, onChange, L }) {
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState("");
  const key = (r) => `${r.type}-${r.id}`;
  const val = (r, field) => edits[key(r)]?.[field] ?? (field === "dob" ? (r.dob || "") : (r.email || ""));
  const setVal = (r, field, v) => setEdits((e) => ({ ...e, [key(r)]: { ...e[key(r)], [field]: v } }));

  const save = async (r) => {
    setBusy(key(r));
    try {
      const payload = { kind: r.type, id: r.id, dob: val(r, "dob") || null };
      if (r.type === "student") payload.student_email = val(r, "email") || null;
      await api.patch("/notifications/birthdate", payload);
      toast.success(L("Saved", "Enregistré"));
      onChange();
    } catch (e) { toast.error(L("Save failed", "Échec de l'enregistrement")); }
    setBusy("");
  };
  const createDemo = async () => {
    try {
      await api.post("/notifications/demo-birthday");
      toast.success(L("Sample created (birthday tomorrow)", "Exemple créé (anniversaire demain)"));
      onChange();
    } catch (e) { toast.error(L("Failed", "Échec")); }
  };
  const delDemo = async (r) => {
    if (!window.confirm(L(`Are you sure you want to delete ${r.name}?`, `Êtes-vous sûr de vouloir supprimer ${r.name} ?`))) return;
    try {
      await api.del(`/notifications/demo/${r.id}`);
      toast.success(L("Sample deleted", "Exemple supprimé"));
      onChange();
    } catch (e) { toast.error(L("Failed", "Échec")); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <p className="text-sm text-muted-foreground">{L("Set birth dates so the system can send automatic birthday letters. Students also need an email.", "Renseignez les dates de naissance pour l'envoi automatique des lettres. Les élèves ont aussi besoin d'un email.")}</p>
        <Button size="sm" variant="outline" onClick={createDemo} data-testid="create-demo-btn"><Plus className="h-4 w-4 mr-1" />{L("Create a sample", "Créer un exemple")}</Button>
      </div>
      {rows.length === 0 ? <Empty title={L("No students or teachers yet", "Aucun élève ni enseignant")} /> : (
        <Table>
          <TableHeader><TableRow><TableHead>{L("Name", "Nom")}</TableHead><TableHead>{L("Type", "Type")}</TableHead><TableHead>{L("Date of birth", "Date de naissance")}</TableHead><TableHead>{L("Email", "Email")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={key(r)} data-testid={`bdate-row-${r.id}`}>
              <TableCell className="font-medium">{r.name}{r.is_demo ? <Badge variant="secondary" className="ml-2">{L("Sample", "Exemple")}</Badge> : null}</TableCell>
              <TableCell>{r.type === "student" ? L("Student", "Élève") : L("Teacher", "Enseignant")}</TableCell>
              <TableCell><Input type="date" className="w-40" value={val(r, "dob")} onChange={(e) => setVal(r, "dob", e.target.value)} data-testid={`bdate-input-${r.id}`} /></TableCell>
              <TableCell>{r.type === "student"
                ? <Input type="email" className="w-52" placeholder="email@exemple.com" value={val(r, "email")} onChange={(e) => setVal(r, "email", e.target.value)} data-testid={`bdate-email-${r.id}`} />
                : <span className="text-xs text-muted-foreground">{r.email || "—"}</span>}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button size="sm" onClick={() => save(r)} disabled={busy === key(r)} data-testid={`bdate-save-${r.id}`}><Save className="h-4 w-4 mr-1" />{L("Save", "Enregistrer")}</Button>
                {r.is_demo && <Button size="icon" variant="ghost" onClick={() => delDemo(r)} data-testid={`bdate-del-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </TableCell>
            </TableRow>))}</TableBody>
        </Table>
      )}
    </div>
  );
}

function History({ history, L }) {
  if (history.length === 0) return <Empty title={L("No notifications yet", "Aucune notification")} />;
  return (
    <Table>
      <TableHeader><TableRow><TableHead>{L("Date", "Date")}</TableHead><TableHead>{L("Kind", "Type")}</TableHead><TableHead>{L("Subject", "Objet")}</TableHead><TableHead>{L("Recipients", "Destinataires")}</TableHead><TableHead>{L("Sent", "Envoyés")}</TableHead></TableRow></TableHeader>
      <TableBody>{history.map((n) => (
        <TableRow key={n.id} data-testid={`ntf-row-${n.id}`}>
          <TableCell className="text-xs">{(n.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
          <TableCell>{n.kind === "birthday" ? <Badge className="bg-amber-500">{L("Birthday", "Anniversaire")}</Badge> : <Badge variant="secondary">{L("Manual", "Manuel")}</Badge>}</TableCell>
          <TableCell>{n.subject}{n.recipient_name ? ` — ${n.recipient_name}` : ""}</TableCell>
          <TableCell>{n.recipient_count ?? "—"}</TableCell>
          <TableCell className="font-medium">{n.sent ?? 0}{n.failed ? ` (${n.failed} ${L("failed", "échoués")})` : ""}</TableCell>
        </TableRow>))}</TableBody>
    </Table>
  );
}
